import AddIcon from "@mui/icons-material/Add";
import { Box, Button } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CustomSelect from "../../../../../components/CustomSelect";
import CustomSelectScroll from "@/components/CustomSelectScroll";
import Flex from "../../../../../components/Flex";
import { useDebounce } from "../../../../../hooks/use-debounce";
import useDisclosure from "../../../../../hooks/use-disclosure";
import ToggleTaskCalenderViewButton from "../../../../../pages/app/provider/collabration/tasks/task-calendar/ToggleTaskCalenderViewbutton";
import { useProviderListV2 } from "../../../../../queries/use-provider-list-v2";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
} from "../../../../../utils/utils";
import { APPOINTMENT_MODE_FILTER_OPTIONS } from "../constants/filterOptions";
import { useAppointmentContext } from "../context/AppointmentContext";
import PatientPortalAppointMentModal from "../patientAppointmentForm/PatientPortalAppointMentModal";

const AppointmentFilters: React.FC = () => {
  const [providerSearchKey, setProviderSearchKey] = useDebounce<string>("");
  const [providerPagination, setProviderPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: providersList, isFetching: providersListFetching } =
    useProviderListV2({
      pagination: providerPagination,
      searchString: providerSearchKey,
      xTENANTID: getXtenantId(),
    });

  const providerOptions = useMemo(() => {
    return providersList?.data?.content?.map((provider) => ({
      value: provider.uuid,
      label: provider.name,
    }));
  }, [providersList?.data?.content]);

  const patientPortalAppointmentDisclosure = useDisclosure(false);
  const { filters, setFilters, viewMode, setViewMode } =
    useAppointmentContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Sync view mode with route
  useEffect(() => {
    if (location.pathname.includes("/calendar")) {
      setViewMode("calendar");
    } else {
      setViewMode("list");
    }
  }, [location.pathname, setViewMode]);

  const handleViewModeChange = (mode: "list" | "calendar") => {
    setViewMode(mode);
    if (mode === "calendar") {
      navigate("/app/patient/appointments/calendar");
    } else {
      navigate("/app/patient/appointments");
    }
  };

  // Map calendar to calender (typo in ToggleTaskCalenderViewButton component)
  const getViewForToggle = (): "list" | "calender" => {
    return viewMode === "calendar" ? "calender" : "list";
  };

  // Map calender back to calendar
  const handleToggleAlignment = (
    _: React.MouseEvent<HTMLElement>,
    newView: "list" | "calender" | null
  ) => {
    if (newView) {
      const mode = newView === "calender" ? "calendar" : "list";
      handleViewModeChange(mode);
    }
  };

  const handleBookAppointment = () => {
    patientPortalAppointmentDisclosure.open();
  };

  return (
    <>
      <PatientPortalAppointMentModal
        disclosureHost={patientPortalAppointmentDisclosure}
      />
      <Flex
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <Flex gap={2} alignItems="center" flexWrap="wrap">
          <Box sx={{ minWidth: 200 }}>
            <CustomSelectScroll
              placeholder="Provider"
              loading={providersListFetching}
              options={providerOptions}
              value={filters.provider}
              onSearch={(_, val) => {
                setProviderSearchKey(val);
                setProviderPagination((p) => ({ ...p, page: 0 }));
              }}
              pagination={providerPagination}
              setPagination={setProviderPagination}
              totalElements={providersList?.data?.page?.totalElements}
              onChange={(value) => setFilters({ provider: value || "all" })}
            />
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <CustomSelect
              placeholder="Appointment Mode"
              options={APPOINTMENT_MODE_FILTER_OPTIONS}
              value={filters.appointmentMode}
              onChange={(value) =>
                setFilters({ appointmentMode: value || "all" })
              }
            />
          </Box>
          <ToggleTaskCalenderViewButton
            view={getViewForToggle()}
            handleAlignment={handleToggleAlignment}
          />
        </Flex>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleBookAppointment}
        >
          Book Appointment
        </Button>
      </Flex>
    </>
  );
};

export default AppointmentFilters;
